import { createUniqueId, Show } from 'solid-js';

const WIDTH = 120;
const HEIGHT = 26;

/** A small fluid line chart: gradient-filled area + stroked polyline + endpoint dot. */
export function Sparkline(props: { points: number[]; color?: string; class?: string }) {
  const gradientId = `spark-grad-${createUniqueId()}`;
  const color = () => props.color ?? 'var(--accent)';

  const coords = () => {
    const pts = props.points;
    if (pts.length < 2) return [] as Array<{ x: number; y: number }>;

    let min = pts[0]!;
    let max = pts[0]!;
    for (const p of pts) {
      if (p < min) min = p;
      if (p > max) max = p;
    }
    const range = max - min;

    return pts.map((p, i) => {
      const x = (i / (pts.length - 1)) * WIDTH;
      const y = range === 0 ? HEIGHT / 2 : HEIGHT - ((p - min) / range) * HEIGHT;
      return { x, y };
    });
  };

  const polylinePoints = () => coords().map((c) => `${c.x},${c.y}`).join(' ');
  const areaPath = () => {
    const c = coords();
    if (c.length < 2) return '';
    const line = c.map((p) => `${p.x},${p.y}`).join(' L');
    const last = c[c.length - 1]!;
    const first = c[0]!;
    return `M${line} L${last.x},${HEIGHT} L${first.x},${HEIGHT} Z`;
  };
  const lastPoint = () => {
    const c = coords();
    return c.length ? c[c.length - 1]! : null;
  };

  return (
    <svg
      class={`spark${props.class ? ` ${props.class}` : ''}`}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      style={{ display: 'block', width: '100%' }}
      aria-hidden="true"
    >
      <Show when={coords().length >= 2}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color={color()} stop-opacity=".3" />
            <stop offset="1" stop-color={color()} stop-opacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath()} fill={`url(#${gradientId})`} />
        <polyline
          points={polylinePoints()}
          fill="none"
          stroke={color()}
          stroke-width="2"
          stroke-linejoin="round"
        />
        <Show when={lastPoint()}>{(p) => <circle cx={p().x} cy={p().y} r="2.6" fill={color()} />}</Show>
      </Show>
    </svg>
  );
}
